"use client";

import { Popover, Transition } from "@headlessui/react";
import { ChevronDownIcon } from "@heroicons/react/20/solid";
import {
  ArrowRightIcon,
} from "@heroicons/react/24/outline";
import Link from "next/link";
import Image from "next/image";
import { Fragment, useState } from "react";
import { usePathname } from "next/navigation";

// Featured article (the appraisal process guide)
const featuredArticle = {
  title: "How the Home Appraisal Process Works",
  subtitle: "Step-by-step guide to professional property valuations in Ontario",
  href: "/appraisal-process",
  tag: "Complete Guide",
  // Uses rhombus-style images like the hero
  images: [
    "/1920px-Toronto_by_Sentinel-2.jpg",
    "/brampton_aerial_view.jpg",
    "/etobicoke-skyline.jpg",
  ],
};

const blogPosts = [
  {
    title: "Home Appraisal Fee & Cost Guide",
    image: "/home-appraisal-cost-2026-cover.png",
    slug: "home-appraisal-fee-cost",
    tag: "Updated",
  },
  {
    title: "What Is a Home Appraisal?",
    image: "/what-is-a-home-appraisal-alpha-appraisals-cover.png",
    slug: "what-is-a-home-appraisal",
    tag: "Guide",
  },
  {
    title: "Probate & Estate Appraisals in Toronto",
    image: "/probate-estate-appraisal-cover-v3.jpg",
    href: "/probate-estate-appraisal",
    tag: "New",
  },
  {
    title: "Divorce & Matrimonial Home Appraisals",
    image: "/divorce-appraisal-cover-v2.jpg",
    href: "/divorce-appraisal",
    tag: "New",
  },
  {
    title: "Litigation Appraiser for Real Estate Disputes",
    image: "/litigation-apppraiser-real-estate.jpg",
    slug: "litigation-appraiser-real-estate",
    tag: "New",
  },
  {
    title: "Capital Gains Tax Appraisals in Ontario",
    image: "/capital-gains-tax-appraisals.jpg",
    slug: "capital-gains-tax-appraisal",
    tag: "Featured",
  },
  {
    title: "Power of Sale Appraisals",
    image: "/power-of-sale-house-appraisals.jpg",
    slug: "power-of-sale-appraisals",
    tag: null,
  },
];

export default function MegaMenu() {
  const [copied, setCopied] = useState(false);
  const pathname = usePathname();
  const isOnNewsPage = pathname?.startsWith("/appraisal-news");

  const copyEmail = () => {
    navigator.clipboard.writeText("info@alphaappraisals.ca");
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Popover className="relative hidden lg:block ml-8">
      {({ open, close }) => (
        <>
          <Popover.Button
            className={`
              relative inline-flex items-center gap-x-1 text-lg font-bold leading-6
              ${open || isOnNewsPage ? "text-blue-600 dark:text-blue-400" : "text-black dark:text-blue-500"}
              hover:text-blue-600 dark:hover:text-blue-400 outline-none
              px-4 py-2
              before:w-[calc(100%-2rem)] before:h-2 before:rounded before:origin-left before:transition-transform before:duration-300 before:bg-blue-500 before:absolute before:left-4 before:top-2 before:transform before:rotate-[320deg]
              ${open || isOnNewsPage ? "before:scale-x-100" : "before:scale-x-0"}
              hover:before:scale-x-100 hover:before:origin-left
            `}
          >
            <span>Recent News</span>
            <ChevronDownIcon
              className={`h-5 w-5 transition-transform duration-200 ${open ? "rotate-180" : ""}`}
              aria-hidden="true"
            />
          </Popover.Button>

          {/* Backdrop overlay to close on outside click */}
          <Transition
            as={Fragment}
            enter="transition ease-out duration-200"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition ease-in duration-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Popover.Overlay className="fixed inset-0 bg-black/20 z-40" />
          </Transition>

          <Transition
            as={Fragment}
            enter="transition ease-out duration-200"
            enterFrom="opacity-0 -translate-y-1"
            enterTo="opacity-100 translate-y-0"
            leave="transition ease-in duration-150"
            leaveFrom="opacity-100 translate-y-0"
            leaveTo="opacity-0 -translate-y-1"
          >
            <Popover.Panel className="fixed left-1/2 z-50 mt-5 w-[80vw] max-w-6xl -translate-x-1/2 px-4">
              <div className="overflow-hidden rounded-2xl bg-white dark:bg-gray-900 shadow-2xl ring-1 ring-gray-900/5 dark:ring-gray-700">
                {/* Blog Posts Section */}
                <div className="p-6 pb-4">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                      Latest Articles
                    </h3>
                    <Link
                      href="/appraisal-news"
                      onClick={() => close()}
                      className="text-sm font-medium text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-1"
                    >
                      View all articles
                      <ArrowRightIcon className="h-4 w-4" />
                    </Link>
                  </div>

                  {/* Two Column Layout: Featured on Left, Blog Posts on Right */}
                  <div className="flex gap-5">
                    {/* Featured Article - Left Side with Rhombus Images */}
                    <Link
                      href={featuredArticle.href}
                      onClick={() => close()}
                      className="group flex-shrink-0 w-56"
                    >
                      <div className="relative h-40 rounded-xl overflow-hidden border-2 border-blue-500 shadow-lg shadow-blue-500/20">
                        {/* Rhombus-style 3 images background */}
                        <div className="absolute inset-0 flex -skew-x-6 scale-110 origin-center">
                          {featuredArticle.images.map((src, index) => (
                            <div key={index} className="w-1/3 h-full relative overflow-hidden">
                              <Image
                                src={src}
                                alt=""
                                fill
                                className="object-cover skew-x-6 scale-110 group-hover:scale-125 transition-transform duration-500"
                              />
                            </div>
                          ))}
                        </div>
                        {/* Overlay */}
                        <div className="absolute inset-0 bg-gradient-to-t from-blue-900/90 via-blue-900/50 to-blue-900/30 group-hover:from-blue-800/95 transition-all duration-300" />

                        {/* Tag */}
                        <span className="absolute top-3 left-3 px-2.5 py-1 text-xs font-bold rounded-md bg-blue-500 text-white">
                          {featuredArticle.tag}
                        </span>

                        {/* Content */}
                        <div className="absolute bottom-0 left-0 right-0 p-3">
                          <h4 className="text-sm font-semibold text-white">
                            How the Home<br />Appraisal Process Works
                          </h4>
                        </div>
                      </div>
                    </Link>

                    {/* Blog Posts - Right Side Ticker Carousel */}
                    <div className="flex-1 overflow-hidden relative">
                      {/* Fade edges */}
                      <div className="absolute left-0 top-0 bottom-0 w-8 bg-gradient-to-r from-white dark:from-gray-900 to-transparent z-10 pointer-events-none"></div>
                      <div className="absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-white dark:from-gray-900 to-transparent z-10 pointer-events-none"></div>

                      <div className="flex gap-4 animate-ticker hover:pause-animation">
                        {/* Double the posts for seamless loop */}
                        {[...blogPosts, ...blogPosts].map((post, index) => (
                          <Link
                            key={`${post.slug || post.href}-${index}`}
                            href={post.href || `/appraisal-news/${post.slug}`}
                            onClick={() => close()}
                            className="group flex-shrink-0 w-56"
                          >
                            <div className="relative h-40 rounded-xl overflow-hidden">
                              <Image
                                src={post.image}
                                alt={post.title}
                                fill
                                className="object-cover transition-transform duration-300 group-hover:scale-110"
                              />
                              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-black/0 group-hover:from-blue-600/80 transition-all duration-300" />

                              {post.tag && (
                                <span className={`absolute top-2 left-2 px-2 py-1 text-xs font-bold rounded-md ${
                                  post.tag === "New"
                                    ? "bg-green-500 text-white"
                                    : post.tag === "Popular"
                                    ? "bg-yellow-500 text-black"
                                    : "bg-blue-500 text-white"
                                }`}>
                                  {post.tag}
                                </span>
                              )}

                              <div className="absolute bottom-0 left-0 right-0 p-3">
                                <h4 className="text-sm font-semibold text-white line-clamp-2">
                                  {post.title}
                                </h4>
                              </div>
                            </div>
                          </Link>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Bottom CTA Bar */}
                <div className="border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 px-6 py-4 rounded-b-2xl">
                  <div className="flex items-center justify-between gap-6">
                    {/* Left side - Profile and text */}
                    <Link
                      href="/about/mushtaq-khan"
                      className="group flex items-center gap-3 rounded-xl focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                    >
                      <Image
                        src="/mushtaq_khan_alpha_appraisals.webp"
                        alt="Mushtaq Khan"
                        width={40}
                        height={40}
                        className="rounded-full ring-2 ring-blue-500/50 transition group-hover:ring-blue-400"
                      />
                      <p className="text-sm text-gray-700 dark:text-gray-300">
                        <span className="font-medium text-gray-900 transition group-hover:text-blue-600 dark:text-white dark:group-hover:text-blue-300">Need a certified residential appraisal?</span>
                        {" "}Meet Mushtaq Khan, P.App., CRA.
                      </p>
                    </Link>

                    {/* Right side - Contact options */}
                    <div className="flex items-center gap-2">
                      {/* Phone */}
                      <a
                        href="tel:416-450-2336"
                        className="inline-flex items-center gap-2 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                        </svg>
                        (416) 450-2336
                      </a>

                      {/* Email with copy button */}
                      <div className="flex items-center bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg overflow-hidden">
                        <a
                          href="mailto:info@alphaappraisals.ca"
                          className="inline-flex items-center gap-2 px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                          </svg>
                          info@alphaappraisals.ca
                        </a>
                        <button
                          onClick={copyEmail}
                          className="px-2 py-2 border-l border-gray-200 dark:border-gray-600 text-gray-500 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                          title="Copy email"
                        >
                          {copied ? (
                            <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                          ) : (
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                            </svg>
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </Popover.Panel>
          </Transition>
        </>
      )}
    </Popover>
  );
}
